import React from 'react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Badge } from './ui/badge';
import { Slider } from './ui/slider';
import { Label } from './ui/label';
import { Card } from './ui/card';
import { Checkbox } from './ui/checkbox';
import { Search, X, Filter } from 'lucide-react';
import { BatchFilters as BatchFiltersType } from './BatchApproval';

interface BatchFiltersProps {
  filters: BatchFiltersType;
  onFiltersChange: (filters: BatchFiltersType) => void;
}

export function BatchFilters({ filters, onFiltersChange }: BatchFiltersProps) {
  // Predefined auditor list - these should match the auditors in your system
  const auditors = [
    'John Smith',
    'Sarah Johnson', 
    'Mike Chen',
    'Lisa Davis',
    'Dr. Brown',
    'Dr. Emily Martinez',
    'Dr. James Park',
    'NP Mary Johnson',
    'Dr. Kevin Brown',
    'NP Lisa Chen'
  ];

  const handleClearFilters = () => {
    onFiltersChange({
      confidenceMin: 70,
      confidenceMax: 100,
      clinician: '',
      auditor: '',
      status: ['Pending'],
      aiSuggestion: [],
      searchQuery: ''
    });
  };

  const handleStatusChange = (status: string, checked: boolean) => {
    const newStatus = checked
      ? [...filters.status, status]
      : filters.status.filter(s => s !== status);
    onFiltersChange({ ...filters, status: newStatus });
  };

  const handleAiSuggestionChange = (suggestion: string, checked: boolean) => {
    const newSuggestions = checked
      ? [...filters.aiSuggestion, suggestion]
      : filters.aiSuggestion.filter(s => s !== suggestion);
    onFiltersChange({ ...filters, aiSuggestion: newSuggestions });
  };

  const activeFiltersCount = [
    filters.searchQuery ? 1 : 0,
    filters.clinician ? 1 : 0,
    filters.auditor ? 1 : 0,
    filters.status.length !== 1 || filters.status[0] !== 'Pending' ? 1 : 0,
    filters.aiSuggestion.length > 0 ? 1 : 0,
    filters.confidenceMin !== 70 || filters.confidenceMax !== 100 ? 1 : 0,
  ].reduce((sum, count) => sum + count, 0);

  return (
    <Card className="p-6 space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Filter className="w-4 h-4 text-gray-500" />
          <h3 className="font-medium text-gray-900" style={{ fontSize: '14px' }}>
            Filters
          </h3>
          {activeFiltersCount > 0 && (
            <Badge variant="secondary" style={{ fontSize: '10px' }}>
              {activeFiltersCount} active
            </Badge>
          )}
        </div>
        
        {activeFiltersCount > 0 && (
          <Button
            variant="ghost"
            size="sm"
            onClick={handleClearFilters}
            className="text-gray-500 hover:text-gray-700"
            style={{ fontSize: '11.2px' }}
          >
            <X className="w-3 h-3 mr-1" />
            Clear all
          </Button>
        )}
      </div>

      <div className="grid grid-cols-1 md:grid-cols-6 gap-4">
        {/* Search */}
        <div className="space-y-2">
          <Label style={{ fontSize: '11.2px', fontWeight: 500 }}>Search</Label>
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-3 h-3 text-gray-400" />
            <Input
              placeholder="Client, clinician, goal..."
              value={filters.searchQuery}
              onChange={(e) => onFiltersChange({ ...filters, searchQuery: e.target.value })}
              className="pl-9"
              style={{ fontSize: '11.2px' }}
            />
          </div>
        </div>

        {/* Clinician Filter */}
        <div className="space-y-2">
          <Label style={{ fontSize: '11.2px', fontWeight: 500 }}>Clinician</Label>
          <Input
            placeholder="Filter by clinician name"
            value={filters.clinician}
            onChange={(e) => onFiltersChange({ ...filters, clinician: e.target.value })}
            style={{ fontSize: '11.2px' }}
          />
        </div>

        {/* Auditor Filter */}
        <div className="space-y-2">
          <Label style={{ fontSize: '11.2px', fontWeight: 500 }}>Auditor</Label>
          <Select
            value={filters.auditor}
            onValueChange={(value) => onFiltersChange({ ...filters, auditor: value === 'all' ? '' : value })}
          >
            <SelectTrigger style={{ fontSize: '11.2px' }}>
              <SelectValue placeholder="All auditors" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All auditors</SelectItem>
              {auditors.map((auditor) => (
                <SelectItem key={auditor} value={auditor}>
                  {auditor}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        {/* Status Filter */}
        <div className="space-y-2">
          <Label style={{ fontSize: '11.2px', fontWeight: 500 }}>Status</Label>
          <div className="space-y-2">
            {['Pending', 'Auto-Approved', 'Auto-Rejected', 'Approved', 'Rejected', 'Skipped'].map((status) => (
              <div key={status} className="flex items-center space-x-2">
                <Checkbox
                  id={`status-${status}`}
                  checked={filters.status.includes(status)}
                  onCheckedChange={(checked) => handleStatusChange(status, !!checked)}
                />
                <Label 
                  htmlFor={`status-${status}`} 
                  className="text-gray-700 cursor-pointer"
                  style={{ fontSize: '10px' }}
                >
                  {status}
                </Label>
              </div>
            ))}
          </div>
        </div>

        {/* AI Suggestion Filter */}
        <div className="space-y-2">
          <Label style={{ fontSize: '11.2px', fontWeight: 500 }}>AI Suggestion</Label>
          <div className="space-y-2">
            {['Compliant', 'Deficient'].map((suggestion) => (
              <div key={suggestion} className="flex items-center space-x-2">
                <Checkbox
                  id={`ai-${suggestion}`}
                  checked={filters.aiSuggestion.includes(suggestion)}
                  onCheckedChange={(checked) => handleAiSuggestionChange(suggestion, !!checked)}
                />
                <Label 
                  htmlFor={`ai-${suggestion}`} 
                  className="text-gray-700 cursor-pointer"
                  style={{ fontSize: '10px' }}
                >
                  {suggestion}
                </Label>
              </div>
            ))}
          </div>
        </div>

        {/* Confidence Range */}
        <div className="space-y-2">
          <Label style={{ fontSize: '11.2px', fontWeight: 500 }}>
            Confidence Range: {filters.confidenceMin}% - {filters.confidenceMax}%
          </Label>
          <div className="space-y-4">
            <div>
              <Label style={{ fontSize: '10px' }} className="text-gray-600">Minimum</Label>
              <Slider
                value={[filters.confidenceMin]}
                onValueChange={([value]) => onFiltersChange({ ...filters, confidenceMin: value })}
                max={100}
                min={0}
                step={5}
                className="mt-1"
              />
            </div>
            <div>
              <Label style={{ fontSize: '10px' }} className="text-gray-600">Maximum</Label>
              <Slider
                value={[filters.confidenceMax]}
                onValueChange={([value]) => onFiltersChange({ ...filters, confidenceMax: value })}
                max={100}
                min={0}
                step={5}
                className="mt-1"
              />
            </div>
          </div>
        </div>
      </div>

      {/* Active Filters Summary */}
      {activeFiltersCount > 0 && (
        <div className="pt-4 border-t border-gray-200">
          <Label style={{ fontSize: '11.2px', fontWeight: 500 }} className="text-gray-600 mb-2 block">
            Active Filters:
          </Label>
          <div className="flex flex-wrap gap-2">
            {filters.searchQuery && (
              <Badge variant="outline" style={{ fontSize: '9.6px' }}>
                Search: "{filters.searchQuery}"
              </Badge>
            )}
            {filters.clinician && (
              <Badge variant="outline" style={{ fontSize: '9.6px' }}>
                Clinician: "{filters.clinician}"
              </Badge>
            )}
            {filters.auditor && (
              <Badge variant="outline" style={{ fontSize: '9.6px' }}>
                Auditor: {filters.auditor}
              </Badge>
            )}
            {(filters.status.length !== 1 || filters.status[0] !== 'Pending') && (
              <Badge variant="outline" style={{ fontSize: '9.6px' }}>
                Status: {filters.status.join(', ')}
              </Badge>
            )}
            {filters.aiSuggestion.length > 0 && (
              <Badge variant="outline" style={{ fontSize: '9.6px' }}>
                AI: {filters.aiSuggestion.join(', ')}
              </Badge>
            )}
            {(filters.confidenceMin !== 70 || filters.confidenceMax !== 100) && (
              <Badge variant="outline" style={{ fontSize: '9.6px' }}>
                Confidence: {filters.confidenceMin}%-{filters.confidenceMax}%
              </Badge>
            )}
          </div>
        </div>
      )}
    </Card>
  );
}